import Portfolio from '@client/models/portfolio';
import Goal from '@client/models/store/goal';
import CurrentGoal from '@client/models/store/goal/current';
import moment from 'moment';
import {Constants} from '@client/util/constants';
// eslint-disable-next-line import/no-cycle
import goalService from '@client/services/goal';
// eslint-disable-next-line import/no-cycle
import investmentService from '@client/services/investment';
// eslint-disable-next-line import/no-cycle
import wealthProjectionService from '@client/services/wealthProjection';
import {Logger, Category} from '@simpleinvest/front-end-logger';
// eslint-disable-next-line import/no-cycle
import {allocationByModel, allocationByGoalModel} from '@client/util/assetAllocationModel';

const logger = new Logger({
  category: Category.Goals,
  fileName: 'goal.js',
});

const MAX_RETIREMENT_AGE = 99;

const stateObj = () => new Goal();

const gettersObj = {
  isValid: (state, getters, rootState, rootGetters) => {
    let result = false;

    if (!state.toAdd) {
      return result;
    }

    switch (state.toAdd.displayType) {
      case 1: {
        result = !!state.toAdd.targetDate;
        break;
      }
      case 2: {
        result = !!(
          state.toAdd.retirementAge &&
          state.toAdd.retirementAge >= rootGetters['user/currentAge'] &&
          state.toAdd.retirementAge <= MAX_RETIREMENT_AGE
        );
        break;
      }
      case 3: {
        result = !!(
          state.toAdd.retirementAge &&
          state.toAdd.retirementAge <= rootGetters['user/currentAge'] &&
          state.toAdd.retirementAge >= MAX_RETIREMENT_AGE
        );
        break;
      }
      default: {
        result = true;
      }
    }

    return result;
  },
  showGoalSelection: (state) => state.selectOptions.length > 0,
  currentGoal: (state) => state.current,
  selectOptions: (state) => state.selectOptions,
  toAdd: (state) => state.toAdd,
  getCurrentGoal: (state) => state.currentGoal,
  toAddPortfolio: (state) => state.toAddPortfolio,
  isGoalSelectionLoaded: (state) => state.isGoalSelectionLoaded || false,
};

const actions = {
  async getWealthProjection({commit}, goalId) {
    const result = await wealthProjectionService.getWealthProjectionByGoalId(goalId);

    commit('SET_WEALTH_PROJECTION', result);
  },
  async getTargetPortfolio({commit}, payload) {
    let result = null;

    result = await (payload.segmentGoalId && !payload.goalId
      ? allocationByModel(payload.portfolioType, payload.segmentGoalId, payload.risk, payload.targetDate)
      : allocationByGoalModel(payload.portfolioType, payload.goalId, payload.risk, payload.targetDate));

    commit('SET_TARGET_PORTFOLIO', result);
  },
  // eslint-disable-next-line consistent-return
  async updateAssetAllocation({commit}, {accountIds}) {
    try {
      const result = await goalService.updateAssetAllocation({accountIds});

      commit('SET_TARGET_PORTFOLIO', result?.data);

      return result?.data;
    } catch (error) {
      logger.logException(error);
    }
  },
  async getCurrentPortfolio({commit}, goalId) {
    try {
      const data = await goalService.getCurrentPortfolio(goalId);

      commit('SET_CURRENT_PORTFOLIO', data);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getCurrentPortfolio',
        message: `Error getting current goal portfolio : ${error.message}`,
      });
      logger.logException({error});
    }
  },
  async getDetails({commit, state, rootGetters}, goalId) {
    commit('CLEAR_CURRENT');

    const clUserId = rootGetters['cognitoUser/userId'];
    const result = await Promise.all([goalService.getGoalDetails(goalId), goalService.fetchAccounts({clUserId, goalId})]);

    // this is horrible but on details call has less info
    // than our goal select call
    // so just pull data from there until we can update that api call
    const goalInfo = state.selectOptions.find(
      (item) =>
        // eslint-disable-next-line radix
        item.cl_goal_id === Number.parseInt(goalId)
    );

    if (goalInfo && result[0] && result[0].length > 0) {
      result[0][0].goalLengthMonths = goalInfo.goalLengthMonths;
      result[0][0].segmentGoalId = goalInfo.segment_goal_Id;
    }

    commit('SET_CURRENT', result);
  },
  async getGoalSelection({commit, rootGetters}) {
    const clUserId = rootGetters['cognitoUser/userId'];
    const response = await goalService.getGoalSelection({clUserId});

    commit('GET_GOAL_SELECTION', response);
    commit('IS_GOAL_SELECTION_LOADED', true);

    return response;
  },
  async updateGoalTarget({commit}, payload) {
    let shouldSetTarget = true;
    let response;

    try {
      response = await goalService.updateGoal(payload);
    } catch (error) {
      shouldSetTarget = false;
      logger.addBreadcrumb({
        callingFuncName: 'updateGoalTarget',
        message: `Error updating goal : ${error.message}`,
      });
      logger.logException({error});
    }

    if (shouldSetTarget) {
      commit('SET_TARGET', payload);
    }

    return response;
  },
  updateOption({commit}, goal) {
    commit('UPDATE_OPTION', goal);
  },
  setToAdd({commit}, goal) {
    commit('SET_TO_ADD', goal);
  },
  setToCurrent({commit}, goal) {
    commit('SET_TO_CURRENT_GOAL', goal);
  },
  setToAddTarget({commit, rootState}, targetValue) {
    // this will interpret what to update based on goal type
    commit('SET_TO_ADD_TARGET', {
      targetValue,
      user: rootState.user.current,
    });
  },
  async getAllocation({commit, rootState, state}, portfolioType) {
    commit('SET_TO_ADD_PORTFOLIO', null);

    if (state.toAdd.targetDate === null && state.toAdd.typeId === 'Model Portfolio') {
      state.toAdd.targetDate = moment().add(20, 'year').format('YYYY-MM-DD');
    }

    if (state.toAdd.targetDate === null) {
      state.toAdd.targetDate = Constants.DEFAULT_TARGET_DATE;
    }

    const response = await allocationByModel(
      portfolioType,
      state.toAdd.segment_goal_Id,
      // eslint-disable-next-line radix
      Number.parseInt(rootState.risk.score),
      moment(state.toAdd.targetDate, 'YYYY-MM-DD').format('YYYYMMDD')
    );

    commit('SET_TO_ADD_PORTFOLIO', response[0]);
  },
  async addGoal({commit, dispatch, state, rootGetters}, reqPayload) {
    let response;
    let payload = {
      segmentGoalId: state.toAdd.segment_goal_Id,
      goalId: state.toAdd.advisorGoalId,
      targetDate: state.toAdd.targetDate,
      retirementAge: state.toAdd.retirementAge,
      displayType: state.toAdd.displayType,
    };

    if (reqPayload) {
      payload = {...payload, ...reqPayload};
    }

    try {
      response = await goalService.saveChosenGoal(payload);
    } catch (error) {
      if (error?.message.includes(500)) {
        throw new Error(error.message);
      }

      throw new Error('Add goal failed');
    }

    const goalId = response.result?.goal_id || state.toAdd.cl_goal_id;

    logger.addBreadcrumb({
      callingFuncName: 'addGoal',
      message: `Goal Id used for attachGoalToAccountRequestId is ${goalId}. response.result.goal_id: ${response.result?.goal_id}  state.toAdd.cl_goal_id: ${state.toAdd.cl_goal_id}`,
    });

    // this is kinda hacky
    // attaches the goal to the accounts during cdo
    const accountRequestIds = rootGetters['account/requests/requestIds'];

    if (accountRequestIds !== null) {
      const attachGoalToAccountRequestIdPromises = [];
      const clUserId = rootGetters['cognitoUser/userId'];

      accountRequestIds.forEach((reqid) => {
        attachGoalToAccountRequestIdPromises.push(
          goalService.attachGoalToAccountRequestId({
            goalId: Number.parseInt(goalId, 10),
            accountRequestId: Number.parseInt(reqid, 10),
            clUserId: Number.parseInt(clUserId, 10),
          })
        );
      });

      await Promise.all(attachGoalToAccountRequestIdPromises);
    }

    dispatch('goalList/setGoalListDirty', null, {root: true});

    commit('CLEAR_TO_ADD');
  },

  // eslint-disable-next-line consistent-return
  async associateGoalWithClient({dispatch, rootGetters}, payload) {
    try {
      const {preSelectedGoalId} = payload;

      // eslint-disable-next-line no-param-reassign
      delete payload.preSelectedGoalId;
      const data = await goalService.associateGoalWithClient(payload);

      if (preSelectedGoalId !== null) {
        const preSelectedGoalIdDoesNotHaveAssociatedAccounts =
          rootGetters['accountList/accountsForGoalId'](preSelectedGoalId).length === 0;

        if (preSelectedGoalIdDoesNotHaveAssociatedAccounts) {
          await dispatch('disassociateGoalWithClient', {
            goalId: preSelectedGoalId,
          });
        }
      }

      return {
        goalId: data.goalId,
      };
    } catch (error) {
      if (error.microserviceStatusCode === 409) {
        await dispatch('updateClientGoal', {
          goalId: error.microserviceResourceId,
          retirementAge: payload.retirementAge,
          targetDate: payload.targetGoalDate,
        });

        return {
          goalId: error.microserviceResourceId,
        };
      }

      logger.addBreadcrumb({
        callingFuncName: 'associateGoalWithClient',
        message: `Error associating goal with client: ${error.message}`,
      });
      logger.logException({error});
    }
  },
  async disassociateGoalWithClient(_, {goalId}) {
    try {
      const data = await goalService.disassociateGoalWithClient({goalId, status: 0});

      return data;
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'disassociateGoalWithClient',
        message: `Error disassociating goal with client: ${error.message}`,
      });
      logger.logException({error});

      return {};
    }
  },
  async updateClientGoal(_, payload) {
    try {
      const data = await goalService.updateClientGoal(payload);

      return data;
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'updateClientGoal',
        message: `Error updating goal for client: ${error.message}`,
      });
      logger.logException({error});

      return {};
    }
  },

  async getAttachAccountToGoal(_, payload) {
    try {
      const response = await goalService.attachAccountToGoal(payload);

      return response;
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getAttachAccountToGoal',
        message: `error attaching account to goalId: ${payload.goalId}`,
      });
      logger.logException({error});

      return {};
    }
  },
  async getAllocationByGoalId(_, payload) {
    const {goalId, riskTolerance, targetDate} = payload;

    try {
      const response = await investmentService.getAllocationByGoalId({
        goalId,
        riskTolerance,
        targetDate,
      });

      return response;
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getAllocationByGoalId',
        message: `Error in getAllocationByGoalId : ${error.message}`,
      });
      logger.logException({error});

      return null;
    }
  },
};

const mutations = {
  UPDATE_OPTION(state, goal) {
    const optionIndex = state.selectOptions.findIndex((option) => option.advisorGoalId === goal.advisorGoalId);

    if (optionIndex !== -1) {
      // eslint-disable-next-line unicorn/prefer-spread
      const optionsCopy = state.selectOptions.slice();

      optionsCopy[optionIndex] = goal;
      state.selectOptions = optionsCopy;
    }
  },
  SET_TARGET(state, goal) {
    state.current.retirementAge = goal.retirementAge;
    state.current.targetDate = goal.targetDate;
  },
  IS_GOAL_SELECTION_LOADED(state, isGoalSelectionLoaded) {
    state.isGoalSelectionLoaded = isGoalSelectionLoaded;
  },
  GET_GOAL_SELECTION(state, goals) {
    // goals targetDate comes in in the format "MMM YYYY" convert to "YYYY-MM-DD"
    // eslint-disable-next-line array-callback-return
    goals.map((goal) => {
      if (goal.targetDate) {
        // eslint-disable-next-line no-param-reassign
        goal.targetDate = moment(goal.targetDate, 'MMM YYYY').format('YYYY-MM-DD');
      }

      if (goal.cl_goal_targetDate) {
        // eslint-disable-next-line no-param-reassign
        goal.cl_goal_targetDate = moment(goal.cl_goal_targetDate, 'MMM YYYY').format('YYYY-MM-DD');
      }
    });
    state.selectOptions = goals;
  },
  SET_TO_ADD(state, goal) {
    state.toAdd = goal;
  },
  SET_TO_CURRENT_GOAL(state, goal) {
    state.currentGoal = goal;
  },
  SET_TO_ADD_TARGET(state, payload) {
    Goal.setTarget(state.toAdd, payload.targetValue, payload.user);
  },
  SET_TO_ADD_PORTFOLIO(state, payload) {
    state.toAddPortfolio = payload ? new Portfolio(payload) : payload;
  },
  CLEAR_TO_ADD(state) {
    state.toAdd = null;
  },
  CLEAR_CURRENT(state) {
    state.current = new CurrentGoal();
  },
  SET_CURRENT(state, payload) {
    state.current.loadData(payload);
    state.current = {...state.current};
  },
  SET_CURRENT_PORTFOLIO(state, payload) {
    state.current.currentPortfolio = new Portfolio(payload[0]);
  },
  SET_TARGET_PORTFOLIO(state, payload) {
    state.current.targetPortfolio = new Portfolio(payload[0]);
  },
  SET_WEALTH_PROJECTION(state, payload) {
    state.current.wealthProjection = payload;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
};
